/**
 * AgentConsole — proposal mockup §2 (page 3): Create a Secure Session Code,
 * Generate / Copy / Copy New / Invalidate, Recent Sessions panel.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { get, post } from '../api'
import { LAST_TENANT_SLUG_KEY } from './last-slug'
import { saveChatSession } from '../chat/session-storage'
import { SessionCountdown } from '../components/SessionCountdown'

interface IssuedCode {
  sessionId: string
  code: string
  expiresAt: number
  emailDelivered?: boolean
}
interface RecentSession {
  id: string
  code: string
  status: string
  createdAt: number
  expiresAt: number
  joinedAt: number | null
  closedAt: number | null
  agentDisplayName: string | null
}
interface MeResponse {
  agent: { displayName: string | null; email: string; role: 'agent' | 'admin' }
  tenant: { id: string; name: string; slug: string; plan: 'trial' | 'pro' } | null
}

const TTL_OPTIONS = [1, 5, 10, 15, 30]

export function AgentConsole() {
  const { slug = '' } = useParams<{ slug: string }>()
  const [me, setMe] = useState<MeResponse | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [ttlMinutes, setTtlMinutes] = useState(10)
  const [customerEmail, setCustomerEmail] = useState('')
  const [sendByEmail, setSendByEmail] = useState(false)
  const [issued, setIssued] = useState<IssuedCode | null>(null)
  const [recent, setRecent] = useState<RecentSession[]>([])
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  // Auto-toggle the "send by email" checkbox to follow whether the email
  // field has a usable value. The user can still manually uncheck it.
  const emailLooksValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim()),
    [customerEmail],
  )
  useEffect(() => {
    setSendByEmail(emailLooksValid)
  }, [emailLooksValid])

  useEffect(() => {
    void get<MeResponse>('/api/agent/me')
      .then((m) => {
        setMe(m)
        if (m.tenant?.slug) {
          localStorage.setItem(LAST_TENANT_SLUG_KEY, m.tenant.slug)
        }
      })
      .catch(() => navigate(`/t/${slug}/agent`))
  }, [navigate, slug])

  useEffect(() => {
    const id = setInterval(refresh, 5000)
    void refresh()
    return () => clearInterval(id)
  }, [])

  async function refresh() {
    try {
      const r = await get<{ sessions: RecentSession[] }>('/api/sessions/recent?limit=20')
      setRecent(r.sessions)
    } catch { /* ignore poll errors */ }
  }

  function emailFieldForRequest(): string | undefined {
    if (!sendByEmail) return undefined
    const trimmed = customerEmail.trim()
    return trimmed.length > 0 ? trimmed : undefined
  }

  async function generate() {
    setError(null)
    try {
      const r = await post<IssuedCode>('/api/sessions', {
        ttlMinutes,
        ...(displayName ? { displayName } : {}),
        ...(emailFieldForRequest() ? { customerEmail: emailFieldForRequest() } : {}),
      })
      setIssued(r)
      void refresh()
    } catch {
      setError('Could not generate a code.')
    }
  }

  async function copy() {
    if (!issued) return
    await navigator.clipboard.writeText(issued.code).catch(() => undefined)
  }

  async function copyNew() {
    if (!issued) return
    setError(null)
    try {
      const r = await post<IssuedCode>(`/api/sessions/${issued.sessionId}/replace`, {
        ttlMinutes,
        ...(displayName ? { displayName } : {}),
        ...(emailFieldForRequest() ? { customerEmail: emailFieldForRequest() } : {}),
      })
      setIssued(r)
      await navigator.clipboard.writeText(r.code).catch(() => undefined)
      void refresh()
    } catch {
      setError('Could not replace.')
    }
  }

  async function invalidate() {
    if (!issued) return
    try {
      await post(`/api/sessions/${issued.sessionId}/invalidate`, {})
      setIssued(null)
      void refresh()
    } catch {
      setError('Could not invalidate.')
    }
  }

  /**
   * Sign the agent out: clears the server-side cookie + the
   * `LAST_TENANT_SLUG_KEY` localStorage entry (Round-2 §2.9). Without
   * the localStorage clear, the next bare `/agent` visit would
   * silently auto-redirect to the just-logged-out tenant — bad UX on
   * shared machines and confusing during account-switching.
   */
  async function logout() {
    try {
      await post('/api/agent/logout', {})
    } catch {
      // Even if the server call fails (network blip), still clear
      // local state — the cookie's HttpOnly flag means we can't
      // delete it from JS, but the next request will get a 401 and
      // the user can re-authenticate cleanly.
    }
    localStorage.removeItem(LAST_TENANT_SLUG_KEY)
    navigate('/agent')
  }

  /**
   * Enter the chatroom as the agent. The server issues a fresh 5-min
   * room-scoped WS token (the cookie-bound access token is NOT enough
   * for the DO upgrade path — the token has to bind sessionId + role).
   */
  async function enterChat(sessionId: string) {
    setError(null)
    try {
      const r = await post<{
        sessionId: string
        wsToken: string
        wsTokenExpiresAt: number
        sessionExpiresAt: number
        subscriberName: string
        agentDisplayName: string | null
      }>(`/api/sessions/${sessionId}/agent-join`, {})
      // Persist the per-session state via the centralised helper (DRY).
      // `sessionExpiresAt` powers the live countdown in the chat header.
      saveChatSession(r.sessionId, {
        wsToken: r.wsToken,
        subscriberName: r.subscriberName,
        agentDisplayName: r.agentDisplayName,
        expiresAt: r.sessionExpiresAt,
      })
      navigate(`/agent/room/${r.sessionId}`)
    } catch {
      setError('Could not enter chatroom.')
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <section className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <header className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-semibold">Create a Secure Session Code</h1>
          <div className="flex items-center gap-3">
            {me?.tenant && (
              <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                me.tenant.plan === 'pro'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-slate-100 text-slate-700'
              }`}>
                {me.tenant.plan === 'pro' ? 'Pro Subscription' : 'Trial'}
              </span>
            )}
            <button
              onClick={logout}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
              aria-label="Sign out and clear remembered tenant slug"
            >
              Sign out
            </button>
          </div>
        </header>
        <p className="text-sm text-slate-600 mb-6">
          Generate a one-time code and read it to the customer. The customer enters it on the SecureBridge page to open a private chat room.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Company">
            <input value={me?.tenant?.name ?? ''} readOnly className={inputClass()} />
          </Field>
          <Field label="Agent Name (optional)">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Maria S."
              className={inputClass()}
            />
          </Field>
          <Field label="Security Policy">
            <select className={inputClass()} disabled defaultValue="standard">
              <option value="standard">Standard (filters + logs)</option>
            </select>
          </Field>
          <Field label="Session Expiration (minutes)">
            <select
              value={ttlMinutes}
              onChange={(e) => setTtlMinutes(Number(e.target.value))}
              className={inputClass()}
            >
              {TTL_OPTIONS.map((n) => (
                <option key={n} value={n}>{n} minutes</option>
              ))}
            </select>
          </Field>
          <Field label="Customer Email (optional)">
            <input
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="customer@example.com"
              type="email"
              autoComplete="off"
              spellCheck={false}
              className={inputClass()}
            />
          </Field>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={sendByEmail}
                onChange={(e) => setSendByEmail(e.target.checked)}
                disabled={!emailLooksValid}
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Send code to customer email
            </label>
          </div>
        </div>

        {issued && (
          <div className="mt-6 p-6 bg-slate-50 border border-slate-200 rounded-xl text-center">
            <div className="font-mono text-3xl tracking-[0.3em] font-semibold text-slate-900">
              {issued.code}
            </div>
            <div className="mt-2 flex items-center justify-center gap-3 text-xs text-slate-500">
              <span>Session: {issued.sessionId.slice(-8)}</span>
              <span aria-hidden>·</span>
              {/*
                Live mm:ss countdown. Replaces the previous static
                `Expires <timestamp>` so the agent can pace the call
                (the customer needs to type the code while it's still
                valid). Colour shifts to amber under 5 min, rose under
                1 min — the agent's cue to hit "Copy New" before the
                code drops.
              */}
              <SessionCountdown expiresAt={issued.expiresAt} size="sm" />
            </div>
            {issued.emailDelivered === true && (
              <div className="mt-2 text-xs text-emerald-700">
                Email sent to {customerEmail.trim()}
              </div>
            )}
            {issued.emailDelivered === false && sendByEmail && (
              <div className="mt-2 text-xs text-amber-700">
                Email send failed — read the code aloud or use Copy.
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <button onClick={generate} className={primaryBtn()}>Generate Code</button>
          <button onClick={copy} disabled={!issued} className={secondaryBtn()}>Copy</button>
          <button onClick={copyNew} disabled={!issued} className={secondaryBtn()}>Copy New</button>
          <button
            onClick={() => issued && enterChat(issued.sessionId)}
            disabled={!issued}
            className={primaryBtn()}
          >
            Enter Chat →
          </button>
          <button onClick={invalidate} disabled={!issued} className={dangerBtn()}>
            Invalidate
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-rose-50 border border-rose-100 rounded-lg text-sm text-rose-900">
            {error}
          </div>
        )}

        <p className="mt-6 text-xs text-slate-500">
          Tip: Have the customer stay on the call and enter this code on the SecureBridge client page.
        </p>
      </section>

      <aside className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Sessions</h2>
        <ul className="text-sm divide-y divide-slate-200">
          {recent.length === 0 && (
            <li className="text-slate-500 italic py-2">No sessions yet</li>
          )}
          {recent.map((s) => {
            const enterable = s.status === 'Pending' || s.status === 'Active'
            return (
              <li
                key={s.id}
                className="py-2 grid grid-cols-[auto_1fr_auto_auto] items-center gap-2"
              >
                <span className="text-slate-500 tabular-nums text-xs">
                  {new Date(s.createdAt).toLocaleTimeString([], {
                    hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                <span className="font-mono text-xs truncate">{s.code}</span>
                <span className={statusClass(s.status)}>{s.status}</span>
                {enterable ? (
                  <button
                    onClick={() => enterChat(s.id)}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Enter
                  </button>
                ) : (
                  <span />
                )}
              </li>
            )
          })}
        </ul>
      </aside>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  )
}

const inputClass = () =>
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

const primaryBtn = () =>
  'rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50'

const secondaryBtn = () =>
  'rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium px-4 py-2 hover:bg-slate-50 disabled:opacity-50'

const dangerBtn = () =>
  'rounded-lg border border-rose-300 bg-white text-rose-700 text-sm font-medium px-4 py-2 hover:bg-rose-50 disabled:opacity-50'

function statusClass(status: string): string {
  const base = 'text-xs font-medium rounded-full px-2 py-0.5'
  switch (status) {
    case 'Active': return `${base} bg-emerald-100 text-emerald-800`
    case 'Pending': return `${base} bg-amber-100 text-amber-800`
    case 'Replaced': return `${base} bg-amber-50 text-amber-700`
    case 'Invalidated':
    case 'Expired':
    case 'Closed':
      return `${base} bg-slate-100 text-slate-600`
    default: return base
  }
}
